import { createSlice, PayloadAction } from '@reduxjs/toolkit';


import { RootState } from './rootState';
import { NormalizedUrl } from '../chrome/normalizeUrl';
import { idsBetween } from '../main/DomWalk';
import { AggregateTagAttachment } from '../main/tagSelector/tag_utils';
import { logger } from '../utils/logger';
import { PageSortOrder } from '../db/pages';
// import { openTabvana } from '../popup/openMain';

export const SEARCH_TAB_INDEX = 7;

export interface UiState {
  expandedTags: string[];
  selectedUrls: string[]; // this is already normalized
  similarUrls: { [key: string]: number };
  similarTags: { [key: string]: AggregateTagAttachment };
  latestSelectionId?: string;
  tabIndex: number;
  searchInput: string;
  queryTokens: string[];
  autoComputeSimilarity: boolean;
  allTabSortOrder: PageSortOrder | null;
  notification: string | null;
  // inspectorSize: number;
}

export const initialState: UiState = {
  expandedTags: [],
  selectedUrls: [],
  similarUrls: {},
  similarTags: {},
  latestSelectionId: undefined,
  tabIndex: 2,
  searchInput: '',
  queryTokens: [],
  autoComputeSimilarity: true,
  allTabSortOrder: null,
  notification: null,
  // inspectorSize: 200,
};

export function constructPageRowId(url: string, windowId?: number, tabId?: number) {
  return `pagerow ${url} ${windowId} ${tabId}`;
}

export function extractUrl(id: string) {
  return id.split(' ')[1];
}

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSelected: (state, action: PayloadAction<{ url: NormalizedUrl; id: string }>) => {
      const { url, id } = action.payload;
      if (state.selectedUrls.includes(url.normalized)) {
        state.selectedUrls = state.selectedUrls.filter((x) => x != url.normalized);
        // if (state.selectedUrls.length == 0) {
        //   state.similarUrls = {};
        //   state.similarTags = {};
        // }
      } else {
        state.selectedUrls.push(url.normalized);
        state.latestSelectionId = id;
      }
    },
    rangeSelected: (
      state,
      action: PayloadAction<{
        url: NormalizedUrl;
        id: string;
      }>
    ) => {
      if (!state.latestSelectionId) {
        // TODO: toggleSelected
        return;
      }
      const allSelectedIds = idsBetween(state.latestSelectionId, action.payload.id);
      const selectedIds = allSelectedIds.filter((id) => id.startsWith('pagerow'));
      logger.debug('[UI] selectedIds', selectedIds);
      state.selectedUrls = selectedIds.map(extractUrl);
    },
    clearSelection: (state) => {
      state.selectedUrls = [];
      state.latestSelectionId = undefined;
      if (state.autoComputeSimilarity) {
        state.similarUrls = {};
        state.similarTags = {};
      }
    },
    clearSelectionAndSelect: (state, action: PayloadAction<{ url: NormalizedUrl; id: string }>) => {
      const { url, id } = action.payload;
      state.latestSelectionId = id;
      state.selectedUrls = [url.normalized];
      if (state.autoComputeSimilarity) {
        state.similarUrls = {};
        state.similarTags = {};
      }
    },
    clearSelectionAndSelectMulti: (state, action: PayloadAction<{ urls: NormalizedUrl[] }>) => {
      const { urls } = action.payload;
      logger.debug('clearSelectionAndSelectMulti');
      state.latestSelectionId = undefined;
      state.selectedUrls = urls.map((url) => url.normalized);
      if (state.autoComputeSimilarity) {
        state.similarUrls = {};
        state.similarTags = {};
      }
    },
    addMultiToSelection: (state, action: PayloadAction<string[]>) => {
      const urls = action.payload;
      const newUrls = urls.filter((url) => !state.selectedUrls.includes(url));
      state.selectedUrls.push(...newUrls);
      if (newUrls.length > 0) {
        state.latestSelectionId = undefined;
      }
    },
    removeMultiFromSelection: (state, action: PayloadAction<string[]>) => {
      const urlsToRemove = action.payload;
      state.selectedUrls = state.selectedUrls.filter((url) => !urlsToRemove.includes(url));
      state.latestSelectionId = undefined;
    },
    setTagExpanded: (state, action: PayloadAction<{ tag: string; expanded: boolean }>) => {
      const { tag, expanded } = action.payload;
      if (expanded) {
        state.expandedTags.push(tag);
      } else {
        state.expandedTags = state.expandedTags.filter((x) => x != tag);
      }
    },
    renameTagInExpanded: (state, action: PayloadAction<{ oldTag: string; newTag: string }>) => {
      const { oldTag, newTag } = action.payload;
      state.expandedTags = state.expandedTags.map((tag) => (tag === oldTag ? newTag : tag));
    },
    focusTag: (state, action: PayloadAction<{ tag: string }>) => {
      const { tag } = action.payload;
      logger.debug('[UI] focusTag', tag);
      state.expandedTags = [tag];
    },
    expandAllTags: (state, action: PayloadAction<{ tags: string[] }>) => {
      state.expandedTags = [...action.payload.tags]; // , 'Untagged'
    },
    collapseAllTags: (state) => {
      state.expandedTags = [];
    },
    setTabIndex: (state, action: PayloadAction<number>) => {
      logger.debug('[UI] setTabIndex', action.payload);
      state.tabIndex = action.payload;
    },
    setSearchInput: (state, action: PayloadAction<string>) => {
      state.searchInput = action.payload;
      state.queryTokens = state.searchInput
        .split(' ')
        .filter((token) => token.length > 0)
        .map((token) => token.toLowerCase());
    },
    newSearch: (state) => {
      state.tabIndex = SEARCH_TAB_INDEX;
      state.searchInput = '';
      state.queryTokens = [];
    },
    setSimilarUrls: (state, action: PayloadAction<{ [key: string]: number }>) => {
      state.similarUrls = action.payload;
    },
    setSimilarTags: (state, action: PayloadAction<{ [key: string]: AggregateTagAttachment }>) => {
      state.similarTags = action.payload;
    },
    setAutoComputeSimilarity: (state, action: PayloadAction<boolean>) => {
      state.autoComputeSimilarity = action.payload;
    },
    setAllTabSortOrder: (state, action: PayloadAction<PageSortOrder | null>) => {
      state.allTabSortOrder = action.payload;
    },
    setNotification: (state, action: PayloadAction<string>) => {
      state.notification = action.payload;
    },
    clearNotification: (state) => {
      state.notification = null;
    },
    // setInspectorSize: (state, action: PayloadAction<number>) => {
    //   state.inspectorSize = action.payload;
    // },
  },

  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
  },
});



export const selectSelectedUrls = (state: RootState) => state.ui.selectedUrls;

export const selectSimilarUrls = (state: RootState) => state.ui.similarUrls;

export const selectIsSelected = (state: RootState, url: string) =>
  state.ui.selectedUrls.includes(url);

export const selectPageSimilarity = (state: RootState, url: string) =>
  state.ui.similarUrls[url] ?? 0;

// export const selectScaledSimilarUrls = (state:RootState) => Object.fromEntries(
//   Object.entries(state.ui.similarUrls).map(([key, value]) => [key, value * scaler])
// );



// export const selectIsExpanded = (tag: string) => (state: RootState) =>
//   state.ui.expandedTags.includes(tag);
// export const selectIsExpanded = (state: RootState) => (tag: string) =>
//   state.ui.expandedTags.includes(tag);
export const selectIsExpanded = (state: RootState, tag: string) =>
  state.ui.expandedTags.includes(tag);
// export const selectIsExpanded = createSelector((state, tag: string) => tag, (state, tag) => {
//   return state.ui.expandedTags.includes(tag);
// });

// export const selectExpandedTags = (state: RootState) => state.ui.expandedTags;

export const isDesynced = (state: RootState) => state.windows.desynced;

export const selectTabIndex = (state: RootState) => state.ui.tabIndex;
export const selectSearchInput = (state: RootState) => state.ui.searchInput;
export const selectQueryTokens = (state: RootState) => state.ui.queryTokens;

export const selectSimilarTags = (state: RootState) => state.ui.similarTags;

export const selectAutoComputeSimilarity = (state: RootState) => state.ui.autoComputeSimilarity;
export const selectAllTabSortOrder = (state: RootState) => state.ui.allTabSortOrder;
export const selectNotification = (state: RootState) => state.ui.notification;

// export const selectInspectorSize = (state: RootState) => state.ui.inspectorSize;

export const {
  toggleSelected,
  rangeSelected,
  clearSelection,
  clearSelectionAndSelect,
  clearSelectionAndSelectMulti,
  addMultiToSelection,
  removeMultiFromSelection,
  renameTagInExpanded,
  focusTag,
  setTagExpanded,
  expandAllTags,
  collapseAllTags,
  setTabIndex,
  setSearchInput,
  newSearch,
  setSimilarUrls,
  setSimilarTags,
  setAutoComputeSimilarity,
  setAllTabSortOrder,
  setNotification,
  clearNotification,
  // setInspectorSize,
} = uiSlice.actions;

export default uiSlice;
